/**
 * Back-in-stock alert shown in place of the buy button on sold-out sizes
 * (TURN Collection only). Klaviyo version.
 *
 *  1. Reveal the inline email row when the button is clicked.
 *  2. On submit, create a Klaviyo back-in-stock subscription on the selected
 *     variant, subscribe the address to the newsletter list, and store the
 *     shopper's language ("Language": fr / en) for the alert email.
 *
 * Visibility and the target variant follow the theme's variantChange event,
 * so switching size keeps the signup and the buy button in sync.
 */
const TURN_KLAVIYO_COMPANY_ID = "TVazWp";
const TURN_KLAVIYO_LIST_ID = "UGBDbZ";
const TURN_KLAVIYO_SOURCE = "TURN Back in stock";
const TURN_KLAVIYO_REVISION = "2024-10-15";

function turnKlaviyoPost(path, payload) {
  return fetch(
    `https://a.klaviyo.com/client/${path}?company_id=${TURN_KLAVIYO_COMPANY_ID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        Accept: "application/vnd.api+json",
        revision: TURN_KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    }
  );
}

class EmailSignup extends HTMLElement {
  constructor() {
    super();

    this.openButton = this.querySelector("[data-email-signup-open]");
    this.formWrapper = this.querySelector("[data-email-signup-form]");
    this.message = this.querySelector("[data-email-signup-message]");
    this.form = this.querySelector("form");

    this.openButton?.addEventListener("click", () => this.reveal());
    this.form?.addEventListener("submit", (event) => this.onSubmit(event));
  }

  reveal() {
    this.openButton.hidden = true;
    this.formWrapper.hidden = false;
    this.form?.querySelector('input[type="email"]')?.focus();
  }

  async onSubmit(event) {
    event.preventDefault();

    const input = this.form.querySelector('input[type="email"]');
    const email = input?.value?.trim();
    if (!email || !input.checkValidity()) {
      this.showMessage(this.dataset.messageInvalid, true);
      return;
    }

    const variantId = this.dataset.variantId;
    if (!variantId) {
      this.showMessage(this.dataset.messageError, true);
      return;
    }

    const submitButton = this.form.querySelector('[type="submit"]');
    submitButton?.setAttribute("disabled", "disabled");

    const language = this.dataset.language === "fr" ? "fr" : "en";
    const profile = { data: { type: "profile", attributes: { email } } };
    const quiet = (label) => (error) => console.error(label, error);

    try {
      // Language first, so it is on the profile whatever happens next.
      const lang = turnKlaviyoPost("profiles", {
        data: {
          type: "profile",
          attributes: { email, properties: { Language: language } },
        },
      }).catch(quiet("Language update failed"));

      const alert = turnKlaviyoPost("back-in-stock-subscriptions", {
        data: {
          type: "back-in-stock-subscription",
          attributes: { channels: ["EMAIL"], profile },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      });

      const newsletter = turnKlaviyoPost("subscriptions", {
        data: {
          type: "subscription",
          attributes: { custom_source: TURN_KLAVIYO_SOURCE, profile },
          relationships: {
            list: { data: { type: "list", id: TURN_KLAVIYO_LIST_ID } },
          },
        },
      }).catch(quiet("Newsletter signup failed"));

      const [alertResponse] = await Promise.all([alert, newsletter, lang]);

      if (alertResponse.ok) {
        this.formWrapper.hidden = true;
        this.showMessage(this.dataset.messageSuccess, false);
      } else {
        this.showMessage(this.dataset.messageInvalid, true);
        submitButton?.removeAttribute("disabled");
      }
    } catch (error) {
      console.error("Back in stock signup failed", error);
      this.showMessage(this.dataset.messageError, true);
      submitButton?.removeAttribute("disabled");
    }
  }

  showMessage(text, isError) {
    if (!this.message) return;
    this.message.textContent = text || "";
    this.message.hidden = !text;
    this.message.classList.toggle("email-signup__message--error", !!isError);
  }

  /** Reset to the collapsed state when the customer changes size. */
  reset() {
    if (this.openButton) this.openButton.hidden = false;
    if (this.formWrapper) this.formWrapper.hidden = true;
    this.showMessage("", false);
    this.form
      ?.querySelector('[type="submit"]')
      ?.removeAttribute("disabled");
  }
}

if (!customElements.get("email-signup")) {
  customElements.define("email-signup", EmailSignup);
}

// Swap between the buy button and the signup as the selected variant changes,
// and point the alert at the newly selected size.
if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const sectionId = event?.data?.sectionId;
    const variant = event?.data?.variant;
    if (!sectionId) return;

    const signup = document.getElementById(`EmailSignup-${sectionId}`);
    if (!signup) return;

    if (variant?.id) signup.dataset.variantId = variant.id;

    const buyable = Boolean(variant && variant.available);
    signup.classList.toggle("hidden", buyable);
    document
      .getElementById(`BuyButtons-${sectionId}`)
      ?.classList.toggle("hidden", !buyable);

    if (!buyable) signup.reset?.();
  });
}
